import {
  composeSelectedCheckboxesValues,
  getSelectedCluster,
} from 'legacy/lut/components/shared/locationTree/helpers/partnerTerritory'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { MultiSelect } from 'core-components/library/multiSelect'
import { Crud } from 'core/types/Crud'
import { PartnerTerritoryHelper } from 'onboarding/classes/helpers/PartnerTerritoryHelper'
import {
  ICluster,
  IMarket,
  IPartnerTerritory,
  IRegion,
} from 'onboarding/classes/models/partnerTerritoryModels'
import { fail } from 'onboarding/classes/services/notification'

import { getCountriesBy, LocationTreeFetcher } from '../LocationTreeFetcher'

export enum LocationMode {
  CountryCode = 'CountryCode',
  CountryName = 'CountryName',
}

export interface ILocationTree {
  partnerTerritory: IPartnerTerritory
  value?: string[]
  onChange?: (value: string[]) => void
  hideHpOrg?: boolean
  className?: string
  label?: string
  limit?: number
  checkFn?: ((value: string) => Promise<string>) | null
  root?: string
  margin?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  mode?: LocationMode
  isUpperCase?: boolean
}

export const LocationTree = memo((props: ILocationTree) => {
  const { partnerTerritory, isUpperCase } = props
  const [mode] = useState(props.mode || LocationMode.CountryCode)
  const [locationItems, setLocationItems] = useState<any[]>([])
  const [marketsList, setMarketsList] = useState<IMarket[]>([])
  const [clustersList, setClustersList] = useState<ICluster[]>([])
  const rootRef = useRef<HTMLDivElement>(null)

  const locationCrud = useMemo(
    () =>
      new Crud<any>({
        fetcher: new LocationTreeFetcher({
          skipHpOrgLevel: props.hideHpOrg,
          partnerTerritory,
        }),
        keyField: 'Id',
        hierarchyField: 'Parent',
        hierarchyRoot: props.root || '',
        defaultPaging: {
          page: 0,
          pageSize: 10000,
        },
      }),
    [props.root, partnerTerritory, props.hideHpOrg]
  )

  const countriesNames = useMemo(() => {
    let result: string[] = []
    if (props.value) {
      if (mode === LocationMode.CountryCode) {
        props.value.forEach((v) => {
          const item = locationItems.find(
            (el) => el.value && el.value.toLowerCase() === v.toLowerCase()
          )
          if (item) {
            result.push(item.name)
          }
        })
      } else {
        result = [...props.value]
      }
    }

    return result
  }, [props.value, mode, locationItems])

  const countriesNamesToDisplay = useMemo(
    () => (isUpperCase ? countriesNames.map((el) => el.toUpperCase()) : countriesNames),
    [countriesNames, isUpperCase]
  )

  const selectedCheckboxesValues = useMemo(
    () => composeSelectedCheckboxesValues({ locationItems, countriesNames, partnerTerritory }),
    [locationItems, countriesNames, partnerTerritory]
  )

  const valueDependencies = useMemo(
    () => [partnerTerritory, marketsList, clustersList],
    [partnerTerritory, marketsList, clustersList]
  )

  const onChange = (value: string[]) => {
    const countries =
      mode === LocationMode.CountryName
        ? value
        : PartnerTerritoryHelper.sortCountries(value, locationItems).map((el) => el.value)

    props.onChange && props.onChange(countries.slice(0, props.limit))
  }

  const checkChildren = (
    item: IForm,
    value: boolean,
    inputted: string,
    partnerTerritory: IPartnerTerritory
  ) => {
    const countries = getCountriesBy(item.Type, item.Title, partnerTerritory)
    if (countries) {
      if (!value && (item.Type === 'HpOrg' || item.Type === 'Market')) {
        for (const country of countries) {
          const checkedCluster = getSelectedCluster(selectedCheckboxesValues, country)
          if (checkedCluster) {
            fail(
              `You can't uncheck ${item.Title}. The countries within it are checked in the ${checkedCluster.key}`
            )

            return
          }
        }
      }
      const filtered = countriesNames.filter(
        (el) => !countries.find((country) => country.name.toLowerCase() === el.toLowerCase())
      )

      if (value) {
        filtered.push(
          ...countries
            .filter((item) => item.name.toLowerCase().includes(inputted.toLowerCase()))
            .map((country) => country.name)
        )
      }
      onChange(filtered)
    } else {
      fail(`You can't check/uncheck the country in the cluster`)
    }
  }

  const onCheck = (item: any, value: boolean, items: any, inputted: string | undefined) => {
    if (item.Type && item.Type === 'Country') {
      if (item.clusters && item.clusters.length > 0) {
        const checkedCluster = getSelectedCluster(selectedCheckboxesValues, item)
        if (checkedCluster) {
          fail(
            `You can't uncheck this country. ${item.name} is in the checked ${checkedCluster.key}`
          )

          return
        }
      }
      const checkedItem = countriesNames.find((el) => el.toLowerCase() === item.name.toLowerCase())
      if (checkedItem) {
        onChange(countriesNames.filter((el) => el.toLowerCase() !== checkedItem.toLowerCase()))
      } else {
        onChange([...countriesNames, item.name])
      }
    } else {
      checkChildren(item, value, inputted || '', partnerTerritory)
    }
  }

  const beforeRemoveHandler = useCallback(
    (item: any) => {
      const countryToDelete = partnerTerritory.countries.find((i) => i.name === item.Title)
      if (!countryToDelete) {
        fail('Failed to remove country')

        return false
      }

      const selectedCluster = getSelectedCluster(selectedCheckboxesValues, countryToDelete)
      if (selectedCluster) {
        fail(
          `You can't uncheck this country. ${countryToDelete.name} is in the checked ${selectedCluster.key}`
        )
      }

      return !selectedCluster
    },
    [selectedCheckboxesValues]
  )

  useEffect(() => {
    const initMarketsAndClusters = () => {
      if (partnerTerritory) {
        let regions = partnerTerritory.regions
        if (props.root) {
          regions = partnerTerritory.regions.filter(
            (region: IRegion) => `Region-${region.region}` === props.root
          )
        }
        const markets: IMarket[] = []
        const clusters: ICluster[] = []
        for (const region of regions) {
          markets.push(
            ...partnerTerritory.markets.filter((market: IMarket) => market.region === region.region)
          )
          clusters.push(
            ...partnerTerritory.clusters.filter(
              (cluster: ICluster) => cluster.region === region.region
            )
          )
        }
        setMarketsList(markets)
        setClustersList(clusters)
      }
    }

    initMarketsAndClusters()
  }, [props.root, partnerTerritory])

  useEffect(() => {
    const fetchLocations = async () => {
      const res = await locationCrud.fetch()
      setLocationItems([...res.items])
    }
    fetchLocations()

    return locationCrud.subscribe('change', () => {
      fetchLocations()
    })
  }, [locationCrud])

  return (
    <div ref={rootRef}>
      <MultiSelect
        disabled={props.disabled}
        margin={props.margin}
        limit={props.limit}
        checkFn={props.checkFn}
        classes={{
          controlClasses: {
            root: props.className,
          },
        }}
        useRowCheckbox
        checkedValues={selectedCheckboxesValues}
        onCheck={onCheck}
        valueDependencies={valueDependencies}
        source={locationCrud}
        caption={'Title'}
        label={props.label}
        beforeRemoveHandler={beforeRemoveHandler}
        onChange={(data) => {
          if (data.length) {
            const ind = countriesNames.findIndex(
              (item) => !data.find((el) => el.toLowerCase() === item.toLowerCase())
            )
            if (ind !== -1) {
              onChange([...countriesNames.slice(0, ind), ...countriesNames.slice(ind + 1)])
            }
          } else {
            onChange([])
          }
        }}
        value={countriesNamesToDisplay}
      />
    </div>
  )
})
